import { apiResponseSchema, idNameSchema, type ApiResponse, type IdName } from '@/api';
import { vesselJobCrewDutyRankExSchema } from '@/modules/PMS/api/vessel-job-crew-duty-ranks/schema';
import { z } from 'zod';

// Re-export common types for backward compatibility
export { idNameSchema };
export type { ApiResponse, IdName };

// ============ CUSTOM SCHEMAS ============

// VesselComponentInfo schema with additional fields
export const vesselComponentInfoSchema = z.object({
  AbstractComponentId: z.string().optional(),
  Id: z.string().optional(),
  Name: z.string().optional(),
  VesselId: z.string().optional()
});

export type VesselComponentInfo = z.infer<typeof vesselComponentInfoSchema>;

// ============ ENTITY SCHEMAS ============

export const vesselJobInfoSchema = z.object({
  Id: z.string().optional(),
  Name: z.string().optional()
});

export type VesselJobInfo = z.infer<typeof vesselJobInfoSchema>;

export const vesselJobCoreSchema = z.object({
  AbstractJobId: z.string().optional(),
  ActivityTypeId: z.string().optional(),
  CompanyInstructions: z.string().optional(),
  CreatedBy: z.string().optional(),
  CreatedDate: z.string().optional(),
  CreatedDateLocal: z.string().optional(),
  DeletedBy: z.string().optional(),
  DeletedDate: z.string().optional(),
  DueDateCalculationBasedOnDoneDate: z.boolean().optional(),
  DueDateCalculationBasedOnRunningHours: z.boolean().optional(),
  ExternalCode: z.string().optional(),
  FrequencyNumber: z.number().optional(),
  FrequencyType: z.string().optional(),
  GracePeriod: z.number().optional(),
  Id: z.string().optional(),
  IntervalType: z.string().optional(),
  IsCritical: z.boolean().optional(),
  IsDeleted: z.boolean().optional(),
  IsDryDock: z.boolean().optional(),
  IsEnvironmental: z.boolean().optional(),
  IsSetup: z.boolean().optional(),
  IsSurvey: z.boolean().optional(),
  JobDescription: z.string().optional(),
  MandatorySparePartConsumption: z.boolean().optional(),
  ManufacturerInstructions: z.string().optional(),
  Name: z.string().optional(),
  Owner: z.string().optional(),
  OwnerChanged: z.boolean().optional(),
  RecordState: z.string().optional(),
  RecordVersion: z.number().optional(),
  RequiresOfficeApprovalForCompletion: z.boolean().optional(),
  RequiresRiskAssessment: z.boolean().optional(),
  ResponsibleOfficerId: z.string().optional(),
  SafetyInstructions: z.string().optional(),
  ShowOnlyNextDueInstance: z.boolean().optional(),
  TotalManHours: z.number().optional(),
  UpdatedBy: z.string().optional(),
  UpdatedDate: z.string().optional(),
  UpdatedDateLocal: z.string().optional(),
  VesselComponentId: z.string().optional(),
  WorkflowId: z.string().optional(),
  WorkflowStatus: z.string().optional()
});

export type VesselJobCore = z.infer<typeof vesselJobCoreSchema>;

export const vesselJobSchema = vesselJobCoreSchema.extend({
  AbstractJobName: z.string().optional(),
  ActivityTypeName: z.string().optional(),
  ResponsibleOfficerName: z.string().optional(),
  VesselComponentAbstractComponentId: z.string().optional(),
  VesselComponentName: z.string().optional(),
  VesselId: z.string().optional(),
  VesselName: z.string().optional()
});

export type VesselJob = z.infer<typeof vesselJobSchema>;

export const vesselJobExSchema = vesselJobCoreSchema
  .omit({
    AbstractJobId: true,
    ActivityTypeId: true,
    ResponsibleOfficerId: true,
    VesselComponentId: true
  })
  .extend({
    AbstractJob: idNameSchema.optional(),
    ActivityType: idNameSchema.optional(),
    CrewDutyRanks: z.array(vesselJobCrewDutyRankExSchema).optional(),
    ResponsibleOfficer: idNameSchema.optional(),
    Vessel: idNameSchema.optional(),
    VesselComponent: vesselComponentInfoSchema.optional()
  });

export type VesselJobEx = z.infer<typeof vesselJobExSchema>;

// ============ ARRAY SCHEMAS ============

export const vesselJobCoreArraySchema = z.array(vesselJobCoreSchema);
export const vesselJobArraySchema = z.array(vesselJobSchema);
export const vesselJobExArraySchema = z.array(vesselJobExSchema);
export const vesselJobInfoArraySchema = z.array(vesselJobInfoSchema);
export const idNameArraySchema = z.array(idNameSchema);

// ============ API RESPONSE SCHEMAS ============

export const vesselJobCoreApiResponseSchema = apiResponseSchema(vesselJobCoreSchema);
export const vesselJobApiResponseSchema = apiResponseSchema(vesselJobSchema);
export const vesselJobExApiResponseSchema = apiResponseSchema(vesselJobExSchema);
export const vesselJobInfoApiResponseSchema = apiResponseSchema(vesselJobInfoSchema);

export const vesselJobCoreArrayApiResponseSchema = apiResponseSchema(vesselJobCoreArraySchema);
export const vesselJobArrayApiResponseSchema = apiResponseSchema(vesselJobArraySchema);
export const vesselJobExArrayApiResponseSchema = apiResponseSchema(vesselJobExArraySchema);
export const vesselJobInfoArrayApiResponseSchema = apiResponseSchema(vesselJobInfoArraySchema);
export const idNameArrayApiResponseSchema = apiResponseSchema(idNameArraySchema);

// ============ FLEET JOBS DASHBOARD SCHEMAS ============

export const fleetJobInstanceSchema = z.object({
  Id: z.string(),
  ExternalCode: z.string().nullable().optional(),
  VesselJobId: z.string().nullable().optional(),
  Name: z.string().nullable().optional(),
  IntervalType: z.string().nullable().optional(),
  FrequencyType: z.string().nullable().optional(),
  FrequencyNumber: z.number().nullable().optional(),
  VesselComponentId: z.string().nullable().optional(),
  RequiresOfficeApprovalForCompletion: z.boolean().nullable().optional(),
  IsCritical: z.boolean().nullable().optional(),
  IsDryDock: z.boolean().nullable().optional(),
  IsSurvey: z.boolean().nullable().optional(),
  ResponsibleOfficerId: z.string().nullable().optional(),
  ResponsibleOfficerName: z.string().nullable().optional(),
  ComponentName: z.string().nullable().optional(),
  VesselName: z.string().nullable().optional(),
  ComponentHierarchyPath: z.string().nullable().optional(),
  DueDate: z.string().nullable().optional(),
  RunningHours: z.number().nullable().optional(),
  Status: z.string().nullable().optional(),
  ApprovalStatus: z.string().nullable().optional(),
  PostponementStatus: z.string().nullable().optional(),
  CompletionDate: z.string().nullable().optional(),
  LastDoneDate: z.string().nullable().optional(),
  AbstractJobId: z.string().nullable().optional(),
  MandatorySparePartConsumption: z.boolean().nullable().optional()
});

export type FleetJobInstance = z.infer<typeof fleetJobInstanceSchema>;

export const fleetJobInstanceArraySchema = z.array(fleetJobInstanceSchema);
export const fleetJobInstanceArrayApiResponseSchema = apiResponseSchema(fleetJobInstanceArraySchema);

// ============ APPROVALS DASHBOARD SCHEMAS ============

export const approvalTypeSchema = z.enum(['OfficeApproval', 'Postponement']);
export const approvalStatusSchema = z.string();

export const approvalDashboardItemSchema = z.object({
  Id: z.string(),
  Type: approvalTypeSchema,
  VesselJobInstanceId: z.string().nullable().optional(),
  VesselName: z.string().nullable().optional(),
  JobName: z.string().nullable().optional(),
  Status: approvalStatusSchema.nullable().optional(),
  Reason: z.string().nullable().optional(),
  Comments: z.string().nullable().optional(),
  CreatedBy: z.string().nullable().optional(),
  CreatedDate: z.string().nullable().optional(),
  FromDate: z.string().nullable().optional(),
  ToDate: z.string().nullable().optional(),
  CurrentDueDate: z.string().nullable().optional(),
  TargetDueDate: z.string().nullable().optional(),
  DeferToDryDock: z.boolean().optional()
});

export type ApprovalDashboardItem = z.infer<typeof approvalDashboardItemSchema>;

export const approvalDashboardItemArraySchema = z.array(approvalDashboardItemSchema);
export const approvalDashboardItemArrayApiResponseSchema = apiResponseSchema(approvalDashboardItemArraySchema);
